import { useRef, useState } from 'react';
import { unstable_useId as useId } from '@mui/material';
import {
  GridTripleDotsVerticalIcon,
  GridDensity,
  GridDensityTypes,
  useGridRootProps,
  gridDensityValueSelector,
  gridFilterActiveItemsSelector,
  gridPreferencePanelStateSelector,
  useGridApiContext,
  useGridSelector,
  GridPreferencePanelsValue,
  GridCsvExportOptions,
} from '@mui/x-data-grid-pro';
import {
  ListItemIcon,
  ListItemText,
  IconButton,
  ControllerMenuItem as MenuItem,
  ButtonProps,
  Badge,
  RiFilterFill,
  Menu,
  Text,
  Divider,
} from '../..';

interface GridExportDisplayOptions {
  disableToolbarButton?: boolean;
}

export interface GridCombinedToolbarMenuProps extends ButtonProps {
  csvOptions?: GridCsvExportOptions & GridExportDisplayOptions;
  disableFilterBadge?: boolean;
  disableFilterMenuItem?: boolean;
  disableColumnsMenuItem?: boolean;
  onDensityChange?: (density: GridDensity) => void;
}

export function GridCombinedToolbarMenu(props: GridCombinedToolbarMenuProps) {
  const {
    csvOptions,
    disableFilterBadge,
    disableFilterMenuItem,
    disableColumnsMenuItem,
    onDensityChange,
    onClick,
    ...other
  } = props;
  const apiRef = useGridApiContext();
  const rootProps = useGridRootProps();
  const densityValue = useGridSelector(apiRef, gridDensityValueSelector);
  const preferencePanel = useGridSelector(apiRef, gridPreferencePanelStateSelector);
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const activeFilters = useGridSelector(apiRef, gridFilterActiveItemsSelector);
  const buttonId = useId();
  const menuId = useId();
  const menuButton = useRef<HTMLButtonElement | null>(null);
  const [openMenu, setOpenMenu] = useState(false);

  const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    setOpenMenu(true);
    onClick?.(event);
  };

  const handleMenuClose = () => {
    setOpenMenu(false);
  };

  const handleDensityUpdate = (newDensity: GridDensity) => {
    apiRef.current.setDensity(newDensity);
    onDensityChange && onDensityChange(newDensity);
    setOpenMenu(false);
  };

  const handleMenuKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Tab') {
      event.preventDefault();
    }
    if (event.key === 'Tab' || event.key === 'Escape') {
      handleMenuClose();
    }
  };

  const toggleFilterPanel = () => {
    const { open, openedPanelValue } = preferencePanel;
    if (open && openedPanelValue === GridPreferencePanelsValue.filters) {
      apiRef.current.hideFilterPanel();
    } else {
      apiRef.current.showFilterPanel();
    }
  };

  const toggleColumnsPanel = () => {
    const { open, openedPanelValue } = preferencePanel;
    if (open && openedPanelValue === GridPreferencePanelsValue.columns) {
      apiRef.current.hidePreferences();
    } else {
      apiRef.current.showPreferences(GridPreferencePanelsValue.columns);
    }
  };

  const handleCsvExport = () => {
    apiRef.current.exportDataAsCsv(csvOptions);
  };

  const densityOptions = [
    {
      icon: <rootProps.components.DensityCompactIcon />,
      label: apiRef.current.getLocaleText('toolbarDensityCompact'),
      value: GridDensityTypes.Compact,
    },
    {
      icon: <rootProps.components.DensityStandardIcon />,
      label: apiRef.current.getLocaleText('toolbarDensityStandard'),
      value: GridDensityTypes.Standard,
    },
    {
      icon: <rootProps.components.DensityComfortableIcon />,
      label: apiRef.current.getLocaleText('toolbarDensityComfortable'),
      value: GridDensityTypes.Comfortable,
    },
  ];

  return (
    <>
      <IconButton id={buttonId} size='medium' {...other} onClick={handleMenuOpen} ref={menuButton}>
        <GridTripleDotsVerticalIcon />
      </IconButton>
      <Menu
        open={openMenu}
        anchorEl={menuButton.current}
        anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
        transformOrigin={{ horizontal: 'left', vertical: 'top' }}
        id={menuId}
        onKeyDown={handleMenuKeyDown}
        onClick={handleMenuClose}
      >
        {!rootProps.disableColumnFilter && !disableFilterMenuItem && (
          <MenuItem onClick={toggleFilterPanel}>
            <ListItemIcon>
              {disableFilterBadge ? (
                <rootProps.components.OpenFilterButtonIcon />
              ) : (
                <Badge badgeContent={activeFilters.length} variant='dot' color='primary'>
                  <RiFilterFill fontSize={24} />
                </Badge>
              )}
            </ListItemIcon>
            <ListItemText>{apiRef.current.getLocaleText('toolbarFilters')}</ListItemText>
          </MenuItem>
        )}
        {!rootProps.disableColumnSelector && !disableColumnsMenuItem && (
          <MenuItem onClick={toggleColumnsPanel}>
            <ListItemIcon>
              <rootProps.components.ColumnSelectorIcon />
            </ListItemIcon>
            <ListItemText>{apiRef.current.getLocaleText('toolbarColumns')}</ListItemText>
          </MenuItem>
        )}
        {!csvOptions?.disableToolbarButton && (
          <MenuItem onClick={handleCsvExport}>
            <ListItemIcon>
              <rootProps.components.ExportIcon />
            </ListItemIcon>
            <ListItemText>{apiRef.current.getLocaleText('toolbarExportCSV')}</ListItemText>
          </MenuItem>
        )}

        {/* using array because of console warning not 
           to use fragment as MenuList component child */}
        {!rootProps.disableDensitySelector && [
          <Divider key='divider' light />,
          <Text key='heading' variant='caption' component='div' padding='8px'>
            Table Row Density
          </Text>,
          densityOptions.map<React.ReactElement>((option, index) => (
            <MenuItem
              key={`${index}-${option.value}`}
              onClick={() => handleDensityUpdate(option.value)}
              selected={option.value === densityValue}
            >
              <ListItemIcon>{option.icon}</ListItemIcon>
              <ListItemText>{option.label}</ListItemText>
            </MenuItem>
          )),
        ]}
      </Menu>
    </>
  );
}
